<template>
  <div>
    <Head title="Mahasiswa" />
    <h1 class="mb-8 text-2xl font-bold">Data Mahasiswa</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="w-full max-w-md mr-4" :max-width="560" @reset="reset">
        <label class="block text-gray-700">Periode Beasiswa:</label>
        <select v-model="form.periode" class="form-select mt-1 w-full">
          <option :value="null">Semua Periode</option>
          <option v-for="p in periodes" :key="p.id" :value="p.id">{{ p.nama }}</option>
        </select>
        <label class="block text-gray-700 mt-4">Status Beasiswa:</label>
        <select v-model="form.status" class="form-select mt-1 w-full">
          <option :value="null">Semua Status</option>
          <option v-for="s in statusOptions" :key="s.id" :value="s.id">{{ s.nama }}</option>
        </select>
        <label class="flex items-center mt-4 text-gray-700">
          <input type="checkbox" v-model="form.multi_periode" class="form-checkbox mr-2" true-value="1" :false-value="null" />
          Lebih dari 1 Periode Beasiswa
        </label>
        <label class="flex items-center mt-4 text-gray-700">
          <input type="checkbox" v-model="form.semua_disetujui" class="form-checkbox mr-2" true-value="1" :false-value="null" />
          Semua Status Disetujui
        </label>
        <label class="block text-gray-700 mt-4">Status Login:</label>
        <select v-model="form.last_login" class="form-select mt-1 w-full">
          <option :value="null">Semua</option>
          <option value="logged_in">Pernah Login</option>
          <option value="never_logged_in">Belum Pernah Login</option>
        </select>
      </search-filter>
    </div>
    <div class="overflow-x-auto bg-white rounded-md shadow">
      <table class="w-full whitespace-nowrap">
        <tr class="font-bold text-left">
          <th class="px-6 pt-6 pb-4">Nama Lengkap</th>
          <th class="px-6 pt-6 pb-4">NIM</th>
          <th class="px-6 pt-6 pb-4">Keterangan Periode Beasiswa</th>
          <th class="px-6 pt-6 pb-4">Status Applicant</th>
          <th class="px-6 pt-6 pb-4 cursor-pointer hover:text-indigo-600" @click="sort('last_login_at')">
            Login Terakhir
            <icon v-if="form.sort === 'last_login_at'" :name="form.direction === 'asc' ? 'cheveron-up' : 'cheveron-down'" class="inline-block w-4 h-4 ml-1 fill-gray-400" />
          </th>
          <th class="px-6 pt-6 pb-4">Aksi</th>
        </tr>
        <tr v-for="mhs in mahasiswas.data" :key="mhs.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t">
            <div class="flex items-center px-6 py-4 focus:text-indigo-500">
              <img v-if="mhs.photo" class="block w-5 h-5 mr-2 -my-2 rounded-full" :src="mhs.photo" />
              {{ mhs.fullname }}
              <icon v-if="mhs.deleted_at" name="trash" class="flex-shrink-0 w-3 h-3 ml-2 fill-gray-400" />
            </div>
          </td>
          <td class="border-t">
            <div class="flex items-center px-6 py-4">
              {{ mhs.nim || mhs.username }}
            </div>
          </td>
          <td class="border-t">
            <div class="px-6 py-4">
              <div v-if="mhs.scholar_applications && mhs.scholar_applications.length">
                <div v-for="(app, index) in mhs.scholar_applications" :key="index" class="mb-1">
                  {{ app.keterangan_periode || '-' }}
                </div>
              </div>
              <span v-else class="text-gray-400">-</span>
            </div>
          </td>
          <td class="border-t">
            <div class="px-6 py-4">
              <div v-if="mhs.scholar_applications && mhs.scholar_applications.length">
                <div v-for="(app, index) in mhs.scholar_applications" :key="index" class="mb-1">
                  <span
                    class="inline-block px-2 py-1 text-xs font-semibold rounded-full"
                    :class="{
                      'bg-gray-200 text-gray-700': app.status == 0,
                      'bg-blue-200 text-blue-700': app.status == 1,
                      'bg-yellow-200 text-yellow-700': app.status == 2,
                      'bg-red-200 text-red-700': app.status == 3,
                      'bg-indigo-200 text-indigo-700': app.status == 4,
                      'bg-green-200 text-green-700': app.status == 5,
                      'bg-orange-200 text-orange-700': app.status == 6,
                    }"
                  >
                    {{ app.status_label }}
                  </span>
                </div>
              </div>
              <span v-else class="text-gray-400">-</span>
            </div>
          </td>
          <td class="border-t">
            <div class="flex items-center px-6 py-4">
              <span v-if="mhs.last_login_at">{{ mhs.last_login_at }}</span>
              <span v-else class="text-gray-400">Belum pernah login</span>
            </div>
          </td>
          <td v-if="can('write user')" class="w-px p-4 border-t">
            <Link v-if="mhs.biodata_id" class="rounded-l btn-green-sm" :href="`/biodata/${mhs.biodata_id}/edit`">
              <font-awesome-icon icon="fas fa-magnifying-glass" />
            </Link>
            <Link class="rounded-l btn-indigo-sm" :href="`/mahasiswas/${mhs.id}/edit`">
              <font-awesome-icon icon="fas fa-pencil" />
            </Link>
            <Link class="rounded-r btn-red-sm" @click.prevent="destroy(mhs.id)">
              <font-awesome-icon icon="fas fa-trash" />
            </Link>
          </td>
        </tr>
        <tr v-if="mahasiswas.data.length === 0">
          <td class="px-6 py-4 border-t" colspan="6">Tidak ada data mahasiswa.</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="mahasiswas.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    mahasiswas: Object,
    periodes: Array,
    statusOptions: Array,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        periode: this.filters.periode,
        status: this.filters.status,
        multi_periode: this.filters.multi_periode,
        semua_disetujui: this.filters.semua_disetujui,
        last_login: this.filters.last_login,
        sort: this.filters.sort,
        direction: this.filters.direction,
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/mahasiswas', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    sort(column) {
      if (this.form.sort === column) {
        this.form.direction = this.form.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.form.sort = column
        this.form.direction = 'asc'
      }
    },
    destroy(id) {
      this.$swal.fire({
        title: 'Anda yakin hapus data ini?',
        text: 'Anda tidak akan dapat mengembalikan data yang sudah dihapus!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.delete(`/mahasiswas/${id}`)
        }
      })
    },
  },
}
</script>
